import L from "leaflet";
import type { LatLng } from "./types";
import { osrmProvider } from "./routing/osrm";
import { haversine, buildBufferPolygon, simplifyPolygon, routeSegment } from "./geo";
import { iconStart, iconDest, type RouteContext, drawStopBandOnMap } from "./routing";
import {
  type ParsedPoi,
  fetchDetourInfo,
  renderPois,
  buildPoiContent,
  setPoiList,
  setPoiClickHandler,
  searchPois,
  drawPolygons,
  sortPoisByArrival,
  formatDuration,
  getVisiblePois,
  getFilteredCount,
  rerenderPoiList,
  setShowOnlySelected,
  setSelectedCount,
  setOnVisibilityChange,
  setFilterCheckbox,
  clearSelectedPois,
  restoreSelectedPois,
  notifyVisibilityChange,
  setOnSelectionChange,
} from "./poi";
import { buildSegmentTimes } from "./live/remainingTime";
import { liveStart, liveStop, onLivePositionUpdate, clearLivePoiEtas, isLiveRunning } from "./live";
import {
  fetchDetourRoute,
  drawDetourRoute,
  clearDetourRoute,
  getSelectedPoiId,
} from "./detourRoute";
import {
  startInput,
  destInput,
  routeBtn,
  locBtn,
  routeInfoEl,
  statusEl,
  spinnerEl,
  minTimeEl,
  maxTimeEl,
  showSearchAreaEl,
  showOnlySelectedEl,
  poiPanel,
  poiToggle,
  poiListEl,
  poiCountEl,
  poiRadiusEl,
  diagEl,
  liveModeEl,
  liveProgressEl,
  settingsBtn,
  settingsPanel,
  restoreSettings,
  getMinHours,
  getMaxHours,
  getRadiusKm,
  isShowOnlySelected,
  poiDetourMaxEl,
  setShowOnlySelected as setShowOnlySelectedDom,
  isLiveMode,
  setLiveMode,
  setLiveProgress,
} from "./dom";
import {
  loadSettings,
  loadSession,
  saveSession,
  clearSession,
  clearSettings,
  hasSession,
  serializePoi,
  deserializePoi,
} from "./state/persistence";

import type { Session } from "./state/persistence";

// ─── Map ─────────────────────────────────────────────────────────────────────

const map = L.map("map", { zoomControl: false }).setView([50.04, 20.0], 7);
L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
  attribution: "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
}).addTo(map);

// ─── State ───────────────────────────────────────────────────────────────────

let startMarker:  L.Marker      | null = null;
let destMarker:   L.Marker      | null = null;
let routeLine:    L.Polyline    | null = null;
let stopBand:     L.FeatureGroup | null = null;
const poiLayer   = L.layerGroup().addTo(map);
let polygonFull:  L.Polygon | null = null;
let polygonSimpl: L.Polygon | null = null;
let polygonVisible = false;
let routeCtx:    RouteContext | null = null;
let startCoords: LatLng | null = null;
let endCoords:   LatLng | null = null;
let origGeometry: LatLng[] = [];
let foundPois:  ParsedPoi[] = [];

// ─── Geocoding ───────────────────────────────────────────────────────────────

async function geocode(query: string): Promise<LatLng & { display: string }> {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`Geocoder error: ${res.status}`);
  const data = await res.json();
  if (!data.length) throw new Error(`No results for "${query}"`);
  return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), display: data[0].display_name };
}

// ─── Clear helpers ────────────────────────────────────────────────────────────

function clearAll() {
  if (startMarker) { map.removeLayer(startMarker); startMarker = null; }
  if (destMarker)  { map.removeLayer(destMarker);  destMarker  = null; }
  if (routeLine)   { map.removeLayer(routeLine);   routeLine   = null; }
  if (stopBand)    { map.removeLayer(stopBand);    stopBand    = null; }
  if (polygonFull)  { map.removeLayer(polygonFull);  polygonFull  = null; }
  if (polygonSimpl) { map.removeLayer(polygonSimpl); polygonSimpl = null; }
  polygonVisible = false;
  poiLayer.clearLayers();
  routeCtx = null;
  startCoords = null;
  endCoords = null;
  origGeometry = [];
  foundPois = [];
  clearDetourRoute();
  liveStop();
  liveModeEl.checked = false;
  setLiveProgress("");
  setPoiList([], null!, poiListEl, poiCountEl, map);
  clearSelectedPois();
}

function addMarker(lat: number, lng: number, icon: L.DivIcon, title: string): L.Marker {
  return L.marker([lat, lng], { icon, title }).addTo(map).bindPopup(title);
}

// ─── Session restore ──────────────────────────────────────────────────────────

function restoreSession(s: Session): void {
  // Guard against malformed session data before mutating any state
  if (
    !s ||
    typeof s.startLat !== "number" || typeof s.destLat !== "number" ||
    !Array.isArray(s.routeGeometry) || s.routeGeometry.length < 2 ||
    typeof s.routeDurationS !== "number" || typeof s.routeDistM !== "number"
  ) {
    throw new Error("Nieprawidłowe dane sesji");
  }

  // Restore input fields
  startInput.value = s.startName;
  destInput.value  = s.destName;

  // Restore route geometry
  const geometry: LatLng[] = s.routeGeometry;
  const totalDistM = s.routeDistM;

  startCoords  = { lat: s.startLat, lng: s.startLng };
  endCoords    = { lat: s.destLat,  lng: s.destLng  };
  routeCtx     = { geometry, durationS: s.routeDurationS, totalDistM, segmentTimes: s.segmentTimes ?? [] };
  origGeometry = geometry;

  startMarker = addMarker(s.startLat, s.startLng, iconStart, `Start: ${s.startName}`);
  destMarker  = addMarker(s.destLat,  s.destLng,  iconDest,  `Dest: ${s.destName}`);

  routeLine = L.polyline(
    geometry.map(p => [p.lat, p.lng] as L.LatLngTuple),
    { color: "#22c55e", weight: 3, opacity: 0.8, zIndexOffset: 1000 },
  ).addTo(map);
  map.fitBounds(L.latLngBounds(geometry.map(p => [p.lat, p.lng] as L.LatLngTuple)), { padding: [60, 60], maxZoom: 13 });

  stopBand = drawStopBandOnMap(map, geometry, s.routeDurationS, totalDistM);

  // Restore POIs (already has detour info from the original session)
  foundPois = s.pois.map(deserializePoi);
  sortPoisByArrival(foundPois);

  // Draw search corridor
  const minH     = getMinHours();
  const maxH     = getMaxHours();
  const radiusKm = getRadiusKm();
  const segment  = routeSegment(geometry, s.routeDurationS, minH * 3600, maxH * 3600);
  if (segment.length >= 2) {
    const buffer     = buildBufferPolygon(segment, radiusKm * 1000);
    const simplified = simplifyPolygon(buffer);
    const { full, simplified: sPoly } = drawPolygons(buffer, simplified, map);
    polygonFull  = full;
    polygonSimpl = sPoly;
    if (!polygonVisible) {
      polygonFull  ? map.removeLayer(polygonFull)  : null;
      polygonSimpl ? map.removeLayer(polygonSimpl) : null;
    }
  }

  // POI selection is already loaded from crp_poi_selection by selection.ts on startup.
  // IDs are in _ids before this function runs. buildPoiContent sees correct isPoiSelected() state.
  setPoiList(foundPois, routeCtx, poiListEl, poiCountEl, map);
  rerenderPoiList(); // apply detour/selection filters to the just-rendered list

  // Sync filter checkbox to settings (trigger re-render if needed)
  const v = isShowOnlySelected();
  setShowOnlySelectedDom(v);
  setShowOnlySelected(v);


  // Auto-start live mode if toggle was left in the ON position
  if (isLiveMode()) liveStart(map, routeCtx);

  const distKm = (totalDistM / 1000).toFixed(1);
  const dur    = formatDuration(s.routeDurationS);
  const { selected } = setSelectedCount();
  setRouteInfo(`${distKm} km · ${dur} · ${foundPois.length} POI (zapisana sesja)${selected ? ` · ${selected} wybrane` : ""}`);
  setStatus(`Sesja przywrócona — ${foundPois.length} POI bez ponownego wyszukiwania`, "ok");
}

// ─── Diagnostics overlay ─────────────────────────────────────────────────────

function setDiag(msg: string | null): void {
  diagEl.textContent = msg ?? "";
}

// ─── Init: restore settings + session ────────────────────────────────────────

try {
  const savedSettings = loadSettings();
  if (savedSettings) restoreSettings(savedSettings);
} catch (e) {
  console.error("loadSettings failed:", e);
  setDiag(`Błąd ustawień: ${e}. Wyczyśćono ustawienia.`);
  clearSettings();
}
// Apply saved filter to any POIs already on screen (e.g. session just restored)
if (routeCtx) rerenderPoiList();

// ─── POI visibility/selection callbacks (registered early so restoreSession can fire them) ──

setFilterCheckbox(showOnlySelectedEl);

setOnVisibilityChange(() => {
  if (!routeCtx) return;
  poiLayer.clearLayers();
  renderPois(getVisiblePois(), routeCtx, poiLayer);
  rerenderPoiList();
  updateRouteInfo();
});



setOnSelectionChange(() => {
  if (!routeCtx) return;
  poiLayer.clearLayers();
  renderPois(getVisiblePois(), routeCtx, poiLayer);
  rerenderPoiList();
  updateRouteInfo();
});

// Filter checkbox: persist to localStorage AND trigger POI rerender
showOnlySelectedEl.addEventListener("change", () => {
  const v = showOnlySelectedEl.checked;
  setShowOnlySelectedDom(v); // dom.ts: persists to localStorage
  setShowOnlySelected(v);    // poi/state.ts: calls notifyVisibilityChange → re-renders map
});

// Detour-time cap: re-filter POIs without re-fetching from Overpass
poiDetourMaxEl.addEventListener("change", () => {
  notifyVisibilityChange();
  updateRouteInfo();
});

try {
  if (hasSession()) {
    const s = loadSession();
    if (s) {
      try {
        restoreSession(s);
      } catch (e) {
        console.error("restoreSession failed:", e);
        setDiag(`Błąd przywracania sesji: ${e}. Wyczyśćono sesję.`);
        clearSession();
        clearAll();
      }
    }
  }
} catch (e) {
  console.error("hasSession/loadSession failed:", e);
  setDiag(`Błąd dostępu do sesji: ${e}. Wyczyśćono sesję.`);
  clearSession();
  clearAll();
}

function setStatus(msg: string, kind: "" | "error" | "ok" = "") {
  statusEl.textContent = msg;
  statusEl.className = kind;
}

function setRouteInfo(msg: string) {
  routeInfoEl.textContent = msg;
}

function setBusy(busy: boolean) {
  routeBtn.disabled = busy;
  locBtn.disabled   = busy;
  spinnerEl.classList.toggle("active", busy);
}

// POI panel toggle
poiToggle.addEventListener("click", () => {
  poiPanel.classList.toggle("open");
  poiToggle.style.left = poiPanel.classList.contains("open") ? "300px" : "0";
});

// Live mode toggle
liveModeEl.addEventListener("change", () => {
  if (liveModeEl.checked) {
    if (!routeCtx) { liveModeEl.checked = false; setStatus("Wczytaj trasę, aby włączyć tryb live.", ""); return; }
    onLivePositionUpdate(refreshLivePois);
    liveStart(map, routeCtx);
  } else {
    liveStop();
    clearLivePoiEtas(foundPois);
    refreshLivePois(0);
    setLiveProgress("");
  }
});

// ─── Refresh POI ETAs on each GPS position update (list DOM + map marker popups) ──

function refreshLivePois(elapsedS: number): void {
  for (const poi of foundPois) {
    if (poi.detour?.reachable) {
      poi.liveEtaS = Math.max(0, poi.detour.arrivalS - elapsedS);
      poi.liveMissed = elapsedS > poi.detour.arrivalS;
    } else {
      poi.liveEtaS = undefined;
      poi.liveMissed = undefined;
    }
  }

  // Re-render list with updated liveEtaS values (checkboxes read from selection state, not DOM)
  rerenderPoiList();

  // Rebuild and refresh ALL marker popups in the POI layer (not just open ones)
  poiLayer.eachLayer((layer: L.Layer) => {
    if (!("setPopupContent" in layer)) return;
    const marker = layer as L.Marker;
    const idAttr = (marker.options as L.MarkerOptions & { poiId?: string }).poiId;
    if (!idAttr) return;
    const poi = foundPois.find(p => p.id === idAttr);
    if (!poi) return;
    marker.setPopupContent(buildPoiContent(poi, ""));
  });
}

// Stop band redraw when time window changes
function redrawStopBand() {
  if (!routeCtx || !stopBand) return;
  map.removeLayer(stopBand);
  stopBand = drawStopBandOnMap(map, routeCtx.geometry, routeCtx.durationS, routeCtx.totalDistM);
}
minTimeEl.addEventListener("input", redrawStopBand);
maxTimeEl.addEventListener("input", redrawStopBand);

// Search area polygon toggle
function showSearchArea() {
  polygonVisible = true;
  polygonFull?.addTo(map);
  polygonSimpl?.addTo(map);
}
function hideSearchArea() {
  polygonVisible = false;
  polygonFull  ? map.removeLayer(polygonFull)  : null;
  polygonSimpl ? map.removeLayer(polygonSimpl) : null;
}
showSearchAreaEl.addEventListener("change", () => {
  if (showSearchAreaEl.checked) showSearchArea(); else hideSearchArea();
});

function updateRouteInfo(): void {
  if (!routeCtx) return;
  const distKm = (routeCtx.totalDistM / 1000).toFixed(1);
  const dur    = formatDuration(routeCtx.durationS);
  const { selected, total } = setSelectedCount();
  const unreachable = foundPois.filter(p => !p.detour?.reachable).length;
  const filtered    = getFilteredCount();
  if (selected === 0 || !isShowOnlySelected()) {
    const extras = [unreachable ? `${unreachable} niedostępne` : null, filtered ? `${filtered} przekracza czas zjazdu` : null].filter(Boolean);
    const poiSummary = extras.length ? `${total} POI (${extras.join(", ")})` : `${total} POI`;
    setRouteInfo(`${distKm} km · ${dur} · ${poiSummary}`);
  } else {
    setRouteInfo(`${distKm} km · ${dur} · Wyświetlam ${selected} wybranych z ${total} miejsc`);
  }
}

// ─── Route + POI button ─────────────────────────────────────────────────────

routeBtn.addEventListener("click", async () => {
  const start = startInput.value.trim();
  const dest  = destInput.value.trim();
  if (!start || !dest) { setStatus("Uzupełnij początek i cel.", "error"); return; }

  setBusy(true);
  setStatus("Szukam trasy…", "");
  liveStop();              // stop any running GPS watch before planning a new route
  liveModeEl.checked = false;
  clearAll();
  clearSession(); // wipe old session before making new requests

  try {
    // 1. Geocode + route
    const [startData, destData] = await Promise.all([geocode(start), geocode(dest)]);
    startCoords = { lat: startData.lat, lng: startData.lng };
    endCoords   = { lat: destData.lat,  lng: destData.lng  };
    startMarker = addMarker(startData.lat, startData.lng, iconStart, `Start: ${startData.display}`);
    destMarker  = addMarker(destData.lat,  destData.lng,  iconDest,  `Dest: ${destData.display}`);

    const route = await osrmProvider.route(startCoords, endCoords);
    if (routeLine)   { map.removeLayer(routeLine);   routeLine   = null; }
    if (stopBand)    { map.removeLayer(stopBand);    stopBand    = null; }

    routeLine = L.polyline(
      route.geometry.map(p => [p.lat, p.lng] as L.LatLngTuple),
      { color: "#22c55e", weight: 3, opacity: 0.8, zIndexOffset: 1000 },
    ).addTo(map);
    map.fitBounds(L.latLngBounds(route.geometry.map(p => [p.lat, p.lng] as L.LatLngTuple)), { padding: [60, 60], maxZoom: 13 });

    const totalDistM = route.geometry.slice(1).reduce((s, b, i) => s + haversine(route.geometry[i], b), 0);
    const segmentTimes = buildSegmentTimes(route.geometry, route.steps, route.durationS);
    routeCtx    = { geometry: route.geometry, durationS: route.durationS, totalDistM, segmentTimes };
    stopBand    = drawStopBandOnMap(map, routeCtx.geometry, routeCtx.durationS, routeCtx.totalDistM);
    origGeometry = route.geometry;
    clearDetourRoute();
    setRouteInfo(`${(route.distanceM / 1000).toFixed(1)} km · ${formatDuration(route.durationS)}`);

    // 2. Search POIs
    routeBtn.disabled = true;
    setStatus("szukam POI…", "");
    poiLayer.clearLayers();
    foundPois = [];

    await searchPois(routeCtx, (pois) => { foundPois = pois; }, setStatus);

    if (!foundPois.length) {
      setPoiList([], routeCtx, poiListEl, poiCountEl, map);
      routeBtn.disabled = false;
      saveSession({
        startName:     startInput.value.trim(),
        destName:      destInput.value.trim(),
        startLat:      startCoords!.lat,
        startLng:      startCoords!.lng,
        destLat:       endCoords!.lat,
        destLng:       endCoords!.lng,
        routeGeometry:  origGeometry,
        routeDurationS:  routeCtx!.durationS,
        routeDistM:      routeCtx!.totalDistM,
        segmentTimes:    routeCtx!.segmentTimes,
        pois: [],
      });
      setStatus("Gotowe", "ok");
      return;
    }

    await fetchDetourInfo(foundPois, startCoords!, endCoords!, routeCtx!.durationS);
    sortPoisByArrival(foundPois);

    // Draw search corridor
    const minH     = getMinHours();
    const maxH     = getMaxHours();
    const radiusKm = getRadiusKm();
    const segment  = routeSegment(routeCtx!.geometry, routeCtx!.durationS, minH * 3600, maxH * 3600);
    if (segment.length >= 2) {
      const buffer     = buildBufferPolygon(segment, radiusKm * 1000);
      const simplified = simplifyPolygon(buffer);
      const { full, simplified: sPoly } = drawPolygons(buffer, simplified, map);
      polygonFull  = full;
      polygonSimpl = sPoly;
      if (!polygonVisible) {
        polygonFull  ? map.removeLayer(polygonFull)  : null;
        polygonSimpl ? map.removeLayer(polygonSimpl) : null;
      }
    }

    setPoiList(foundPois, routeCtx, poiListEl, poiCountEl, map);
    renderPois(getVisiblePois(), routeCtx, poiLayer);
    updateRouteInfo();

    setStatus("Gotowe", "ok");

    // Persist session (route + POIs with detour info)
    saveSession({
      startName:      startInput.value.trim(),
      destName:       destInput.value.trim(),
      startLat:       startCoords!.lat,
      startLng:       startCoords!.lng,
      destLat:        endCoords!.lat,
      destLng:        endCoords!.lng,
      routeGeometry:   routeCtx!.geometry,
      routeDurationS:   routeCtx!.durationS,
      routeDistM:       routeCtx!.totalDistM,
      segmentTimes:     routeCtx!.segmentTimes,
      pois: foundPois.map(serializePoi),
    });

  } catch (err) {
    setStatus(String(err), "error");
  } finally {
    setBusy(false);
  }
});

// ─── POI click: fetch + draw detour route ─────────────────────────────────────

setPoiClickHandler(async (poi: ParsedPoi) => {
  if (!startCoords || !endCoords || !origGeometry.length) return;
  if (getSelectedPoiId() === poi.id) { clearDetourRoute(); return; }

  setStatus("Rysuję objazd…", "");
  spinnerEl.classList.add("active");
  try {
    await fetchDetourRoute(poi, startCoords, endCoords);
    drawDetourRoute(poi, startCoords, endCoords, map, origGeometry);
    setStatus("Objazd pokazany");
  } catch (err) {
    setStatus(String(err), "error");
  } finally {
    spinnerEl.classList.remove("active");
  }
});

// ─── Settings panel toggle ───────────────────────────────────────────────────

settingsBtn.addEventListener("click", () => {
  const visible = settingsPanel.style.display !== "none";
  settingsPanel.style.display = visible ? "none" : "";
  settingsBtn.classList.toggle("active", !visible);
});

// ─── Current location ────────────────────────────────────────────────────────

locBtn.addEventListener("click", () => {
  if (!navigator.geolocation) { setStatus("Geolokalizacja niedostępna.", "error"); return; }
  locBtn.disabled = true;
  setStatus("Pobieram lokalizację…", "");
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      startInput.value = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      setStatus(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
      locBtn.disabled = false;
    },
    (err) => { setStatus(`Błąd geolokalizacji: ${err.message}`, "error"); locBtn.disabled = false; },
  );
});
